import { Component, ElementRef, Input, OnInit, Renderer2, ViewChild, ViewEncapsulation } from '@angular/core';
import { MenuEvent } from '@progress/kendo-angular-menu';
import { Observable } from 'rxjs';
import { DestroyBase } from '../../../../components/common/ng-destroy-base';
import { EventBusService } from '../../../../services/event-bus.service';
import { ReportDesignerWidgetConfig } from '../../models/report-designer-widget-config';
import { ReportDesignerHostComponent } from '../../report-designer-host/report-designer-host.component';
import { ReportDesignerStorageService } from '../../services/report-designer-storage.service';
import { WidgetLockerService } from '../../services/WidgetLockerService';
import { reportDesignerWigdets } from '../../widgets/widgetsRegistry';
import { ReportDesignerDocumentComponent } from '../report-designer-document/report-designer-document.component';
import { ReportDesignerPageComponent } from '../report-designer-page/report-designer-page.component';
import { WidgetHostDirective } from '../widget-host/widget-host.directive';

/**
 * Widget box: Dynamically renders widgets and provides an action menu for them
 */
@Component({
  selector: 'widget-box',
  templateUrl: './widget-box.component.html',
  styleUrls: ['./widget-box.component.scss'],
  encapsulation: ViewEncapsulation.None
})
export class WidgetBoxComponent extends DestroyBase implements OnInit {
  /**
   * The widget
   */
  @Input() widget: ReportDesignerWidgetConfig;

  /**
   * The RD host component
   */
  @Input() main: ReportDesignerHostComponent;

  /**
   * The page
   */
  @Input() page: ReportDesignerPageComponent;

  /**
   * The report document
   */
  @Input() public document: ReportDesignerDocumentComponent;

  /**
   * The widget host element
   */
  @ViewChild(WidgetHostDirective, { static: true }) widgetHost: WidgetHostDirective;

  /**
   * Widget header mode subject
   */
  public appWidgetHeaderMode$: Observable<boolean>;

  /**
   * Gets the widget ID
   */
  public get widgetId() {
    return this.widget.widgetSettings.id;
  }

  /**
   * Parallel working (check in/out) enabled?
   */
  public parallelWorking = false;

  /**
   * Hide the check-out notification button?
   */
  public hideCheckoutNotification = false;

  /**
   * Show the comment button?
   */
  public showCommentBtn = true;

  /**
   * Show the delete button?
   */
  public showDeleteBtn = true;

  /**
   * Red button enabled?
   */
  public redButton = false;

  /**
   * Does it have a Kendo editor?
   */
  public hasKendoEditor = false;

  /**
   * Does it have a slide-out?
   */
  public hasSlideOut = false;

  /**
   * The report OID
   */
  public reportId = 0; //Used in UI

  /**
   * Gets the infobar state
   */
  public get buttonInfobar() {
    //if not defined we assume enabledButtonInfobar is true, so check for undefined
    if (this.widget.widgetSettings.enabledButtonInfobar != undefined && !this.widget.widgetSettings.enabledButtonInfobar) {
      return false;
    }
    return true;
  }

  /**
   *
   * Constructor
   * @param rdStorage
   * @param widgetLocker
   * @param eventBus
   * @param renderer2
   */
  constructor(private rdStorage: ReportDesignerStorageService,
    private widgetLocker: WidgetLockerService,
    private eventBus: EventBusService,
    private renderer2: Renderer2,
    public elementRef: ElementRef) {
    super();
  }

  /**
   * Initializes the components
   */
  public ngOnInit(): void {
    this.appWidgetHeaderMode$ = this.rdStorage.appWidgetHeaderMode$;

    this.subscriptions.add(this.rdStorage.report$.subscribe((reportId: number) => {
      this.reportId = reportId;
    }));

    this.onEvents.push(this.eventBus.subscribeEvent('report.designer.widget.locks.changed', () => {
      this.widget.widgetLockInfo = this.widgetLocker.getLockInfoForWidget(this.widget);
    }));

    this.onEvents.push(this.eventBus.subscribeEvent('onWidgetLockChanged', () => {
      this.parallelWorking = true;
    }));

    this.onEvents.push(this.eventBus.subscribeEvent('saveChanges', () => {
      this.parallelWorking = false;
    }));

    this.onEvents.push(this.eventBus.subscribeEvent('commentButtonRed', (item) => {
      if (this.widget.widgetSettings.id == item.widgetConfig.widgetSettings.id) {
        if (!item.emptyComment) {
          this.redButton = true;
        }
        else {
          this.redButton = false;
        }
      }
    }));

    if (this.widget.widgetSettings.hideCheckoutNotification) {
      this.hideCheckoutNotification = true;
    }

    if (this.widget.noComment) {
      this.showCommentBtn = false
    }

    if (this.widget.noDelete) {
      this.showDeleteBtn = false
    }

    if (this.widget.comment != "" && this.widget.comment != undefined) {
      this.redButton = true;
    }

    if (this.widget.widgetSettings) {
      this.hasKendoEditor = this.widget.widgetSettings.hasKendoEditor;
      this.hasSlideOut = this.widget.widgetSettings.hasSlideOut;
    }

    this.widget.widgetLockInfo = this.widgetLocker.getLockInfoForWidget(this.widget);

    const viewContainerRef = this.widgetHost.viewContainerRef;
    const template = String(this.widget.template);
    const nameSelector = template.substring(1, template.indexOf(' ')).split('-').join('').toLowerCase();
    const entryFromRegistry = reportDesignerWigdets.find((widget) => { return widget.downgradedName.toLowerCase() === nameSelector; });
    if (entryFromRegistry) {
      const componentRef = viewContainerRef.createComponent(entryFromRegistry.componentClass);
      componentRef.instance.widgetConfig = this.widget;
      componentRef.instance.page = this.page;
      componentRef.instance.widgetGuid = this.widgetId;
      componentRef.instance.widgetBox = this;
      this.renderer2.addClass(componentRef.location.nativeElement, 'report-designer-widget-instance');
    }
  }

  /**
   * An action item was selected
   * @param event
   */
  public onSelect(event: MenuEvent): void {
    switch (event.item.text) {
      case 'activate': {
        this.activateWidgetHandler();
        break;
      }
      case 'lock': {
        this.tryLockWidget();
        break;
      }
      case 'comment': {
        this.commentWidget();
        break;
      }
      case 'editor': {
        this.toggleKendoEditor();
        break;
      }
      case 'slideout': {
        this.toggleSlideOut();
        break;
      }
      case 'delete': {
        this.deleteWidget();
        break;
      }
    }
  }

  /**
   * Try to lock a widget
   */
  public tryLockWidget(): void {
    if (this.buttonInfobar && !this.widget.widgetLockInfo.locked) {
      this.lockWidget();
    }
    else {
      this.unlockWidget();
    }
  }

  /**
   * Activate the widget
   */
  public activateWidgetHandler(): void {
    this.eventBus.broadcastEvent("activateWidget", this);
  }

  /**
   * Comment the widget
   */
  public commentWidget(): void {
    this.eventBus.broadcastEvent("commentWidget", this);
    this.activateWidgetHandler();
  }

  /**
   * Toggle the Kendo editor state
   */
  public toggleKendoEditor(): void {
    this.eventBus.broadcastEvent("widgetToggleKendoEditor", this.widgetId);
  }

  /**
   * Togggles the slide-out
   */
  public toggleSlideOut(): void {
    this.eventBus.broadcastEvent("widgetToggleSlideOut", this.widgetId);
  }

  /**
   * Deletes the widget
   */
  public deleteWidget(): void {
    const document = this.main.getActiveDocument();
    this.unlockWidget();
    document.deleteWidget(this.widgetId);
  }

  /**
   * Locks the widget
   */
  public lockWidget(): void {
    const reportId = this.rdStorage.getReport();
    const widgetId = this.widget.widgetId;
    const multipleInstances = this.widget.multipleInstances;
    const currentLayout = this.widget.widgetSettings.currentLayout;
    this.widgetLocker.lockWidget(reportId, widgetId, 0, multipleInstances, currentLayout, this.widget).then(() => {
      this.eventBus.broadcastEvent("activateWidget", this);
      this.eventBus.broadcastEvent("onWidgetLockChanged", this, true);
    });
  }

  /**
   * Unlocks the widget
   */
  public unlockWidget(): void {
    const reportId = this.rdStorage.getReport();
    const widgetId = this.widget.widgetId;
    const multipleInstances = this.widget.multipleInstances;
    const currentLayout = this.widget.widgetSettings.currentLayout;
    this.widgetLocker.unlockWidget(reportId, widgetId, 0, multipleInstances, currentLayout).then(() => {
      this.eventBus.broadcastEvent("onWidgetLockChanged", this, false);
    });
  }

  /**
   *
   * Updates the modified info
   * @param widgetData
   */
  public updateModifiedInfo(widgetData: any): void {
    if (widgetData?.changed_by_user_description && widgetData?.changed_by_date) {
      this.widget.widgetModifiedInfo = {
        modifiedByUserName: widgetData.changed_by_user_description,
        modifiedAt: widgetData.changed_by_date
      };
    }
    else {
      this.widget.widgetModifiedInfo = null;
    }
  }
}
